import ReactDOM from 'react-dom/client';
import { waitForCanvasReady } from './canvasLoadPoller';

/**
 * Mount a canvas-drawing React element off screen, in a box of exactly
 * `width` x `height` CSS px, wait for it to draw, and hand back the canvas as
 * a PNG data URL. Always unmounts.
 *
 * The box size IS the canvas's logical size (`updateCanvasSize` reads the
 * container), so callers that need an exact px/ft pass whole px — and the
 * canvas is ready only once its backing store has been sized to that box.
 * Until then it is a default 300x150 element that has drawn nothing, and
 * capturing it would print a blank thumbnail at the wrong scale, so a canvas
 * that never gets there returns null instead.
 */
export async function captureOffscreenCanvas(element, { width, height }) {
  const dpr = window.devicePixelRatio || 1;
  const sizedToBox = (canvas) =>
    canvas.width === Math.round(width * dpr) &&
    canvas.height === Math.round(height * dpr);
  const container = document.createElement('div');
  Object.assign(container.style, {
    position: 'absolute',
    left: '-9999px',
    top: '-9999px',
    width: `${width}px`,
    height: `${height}px`,
  });
  document.body.appendChild(container);
  const root = ReactDOM.createRoot(container);
  try {
    root.render(element);
    const canvas = await waitForCanvasReady(container, {
      isReady: sizedToBox,
    });
    if (!canvas || !sizedToBox(canvas)) {
      console.warn('[offscreenCanvasCapture] canvas never sized to its box', {
        width,
        height,
        dpr,
        canvas: canvas && [canvas.width, canvas.height],
      });
      return null;
    }
    return canvas.toDataURL('image/png');
  } finally {
    root.unmount();
    container.remove();
  }
}
